import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AnalyticsOutlinedIcon from "@mui/icons-material/AnalyticsOutlined";
import { DdlResult } from "../../../../services/api/types/reports/reports";
import { MAX_LENGTH_SHORT_LABEL } from "../../../../utils/inputLimits";

interface ReportConfigurationProps {
  value: {
    reportType: string;
    reportName: string;
  };
  reportTypeOptions: DdlResult | null;
  onChange: (data: { reportType?: string; reportName?: string }) => void;
  errors: Record<string, string>;
  isExpanded: boolean;
}

export const ReportCofiguration: React.FC<ReportConfigurationProps> = ({
  value,
  reportTypeOptions,
  onChange,
  errors,
  isExpanded,
}) => {
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    setExpanded(isExpanded);
  }, [isExpanded]);

  const handleChange = (field: any, newValue: any) => {
    onChange({ [field]: newValue });
  };

  return (
    <Accordion
      elevation={0}
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      sx={{
        borderRadius: "12px",
        backgroundColor: "white",
        border: "1px solid #E5E7EB",
        boxShadow: "none",
        "&:before": { display: "none" }, // remove default divider line
        "&:not(:first-of-type)": {
          borderTopLeftRadius: "12px",
          borderTopRightRadius: "12px",
        },
        "&:last-of-type": {
          borderBottomLeftRadius: "12px",
          borderBottomRightRadius: "12px",
        },
        "&:not(:last-of-type)": {
          borderBottom: "1px solid #E5E7EB",
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          px: 2.5,
          py: 1.5,
          minHeight: 64,
          "& .MuiAccordionSummary-content": {
            alignItems: "center",
            margin: 0,
          },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <AnalyticsOutlinedIcon sx={{ color: "#7C3AED", fontSize: 24 }} />
          <Typography fontSize={18} fontWeight={500}>
            Configuration
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 3, pb: 3 }}>
        <Box sx={{ borderTop: "1px solid #E5E7EB", pt: 2 }}>
          <Box>
            <Stack direction="row" gap={2}>
              <Stack spacing={1} width="50%">
                <Typography variant="subtitle2">
                  Report type
                  <Box component="span" sx={{ color: `var(--error-700)` }}>
                    *
                  </Box>
                </Typography>
                <Select
                  id="report-type-select"
                  IconComponent={ExpandMoreIcon}
                  displayEmpty
                  value={value.reportType || ""}
                  error={Boolean(errors?.reportType)}
                  onChange={(e) => handleChange("reportType", e.target.value)}
                  renderValue={(selected) =>
                    selected ? (
                      // show the selected label
                      reportTypeOptions?.result.find(
                        (option) => option.key === selected,
                      )?.value || selected
                    ) : (
                      // placeholder
                      <Typography color="#6B7280">Select type</Typography>
                    )
                  }
                  sx={{
                    borderRadius: "8px",
                    backgroundColor: "transparent",
                    height: "44px",
                    "&:hover": {
                      backgroundColor: "transparent",
                    },
                    "& .MuiSelect-select": {
                      display: "flex",
                      alignItems: "center",
                      padding: "8.5px 14px",
                    },
                  }}
                  size="medium"
                >
                  {reportTypeOptions?.result.map((option) => (
                    <MenuItem key={option.key} value={option.key}>
                      {option.value}
                    </MenuItem>
                  ))}
                </Select>
                {errors?.reportType && (
                  <Typography variant="caption" color="error">
                    {errors.reportType}
                  </Typography>
                )}
              </Stack>
              <Stack spacing={1} width="50%">
                <Typography variant="subtitle2">
                  Report name
                  <Box component="span" sx={{ color: `var(--error-700)` }}>
                    *
                  </Box>
                </Typography>
                <TextField
                  value={value.reportName || ""}
                  onChange={(e) => handleChange("reportName", e.target.value)}
                  size="small"
                  placeholder="Insert name"
                  error={Boolean(errors?.reportName)}
                  helperText={errors?.reportName}
                  slotProps={{
                    htmlInput: { maxLength: MAX_LENGTH_SHORT_LABEL },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      height: 44,
                    },
                  }}
                />
              </Stack>
            </Stack>
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};
